import React, { useState } from 'react';
import {
  Accordion,
  ChakraBox,
  ChakraFlex,
  ChakraSimpleGrid,
  CheckboxV2,
  DatePickerInput,
  FAIconName,
  FireflyButton,
  GroupClassIllustrations,
  Icon,
  InputGroupV2,
  Text,
  TextInputV2,
  UniversalSelect,
  ValidationMessage,
} from '@equip.health/ui';
import { isEmpty, isEqual } from 'lodash';
import { PROMOTE_THIS_GROUP_IN_EQUIP_APP } from '~/lib/constants/groupClasses.constants';

import {
  ALL_CLASSES_REQUIRED_V2,
  GROUP_CLASS_DESCRIPTION_LIMIT,
  GROUP_CLASS_DURATIONS,
  GROUP_CLASS_TITLE_LIMIT,
  REGISTRATION_ENDS,
  SENSITIVE_GROUP_CLASS_LABEL,
} from '~/lib/constants/calendar.constants';
import { TextInputV2Type } from '~/lib/constants/inquiry.constants';
import { ValidationType } from '~/lib/constants/enumeration.constants';
import {
  Recurrence,
  roundDateToNearestInterval,
} from '~/lib/util/calendar.util';
import {
  ACCORDION_HEIGHT_CALCULATED,
  ACCORDION_WIDTH_LARGE,
} from '~/lib/constants';
import GroupClassConfirmation, {
  ConfirmationType,
} from './GroupClassConfirmation';
import DiscardChangesModalV2 from '~/components/common/DiscardChangesModalV2';
import RemoveSensitiveMarkConfirmationModal from './modals/RemoveSensitiveMarkConfirmationModal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import { groupClassHostToAutoCompleteOption } from '~/lib/util/user.util';
import { DateTime } from 'luxon';
import { formatFullDateString } from '~/lib/util/schedule/schedule.util';
import { wasDateChanged } from '~/lib/util/date.util';
import { useGroupClassAttendeesLimit } from '~/lib/hooks/useGroupClassAttendeesLimit';
import { GroupClassTitleField } from '~/components/maud/calendar/group-class/GroupClassTitleField';
import { GroupClassDescriptionField } from '~/components/maud/calendar/group-class/GroupClassDescriptionField';
import { GroupClassHostsField } from '~/components/maud/calendar/group-class/GroupClassHostsField';
import { GroupIcons } from '~/components/maud/calendar/group-class/GroupIcons';
import { GroupClassRecurrenceField } from '~/components/maud/calendar/group-class/GroupClassRecurrenceField';
import { GroupClassAttendeeTypesField } from '~/components/maud/calendar/group-class/GroupClassAttendeeTypesField';
import { GroupClassTreatmentProgramsField } from '~/components/maud/calendar/group-class/GroupClassTreatmentProgramsField';
import { GroupClassModalitiesField } from '~/components/maud/calendar/group-class/GroupClassModalitiesField';
import { GroupClassDiagnosisField } from '~/components/maud/calendar/group-class/GroupClassDiagnosisField';
import { GroupClassAgeRangeField } from '~/components/maud/calendar/group-class/GroupClassAgeRangeField';

type GroupClassFormValues = {
  ageRanges: string[];
  attendeeRoles: string[];
  description: string;
  diagnoses: string[];
  duration?: number;
  endDate?: Date;
  frequency?: Recurrence;
  hosts: GroupClassHost[];
  iconName?: string;
  isAllClassesRequired: boolean;
  isPromotedInEquipApp: boolean;
  isRegistrationEndsAfterFirstClass: boolean;
  isSensitive: boolean;
  maximumAttendees?: number;
  startDate?: Date;
  title: string;
  treatmentModalities: string[];
  treatmentPrograms: string[];
};

export type { GroupClassFormValues };

export type GroupClassAccordionProps = {
  mode: 'add' | 'edit';
  initialValues?: GroupClassExtended;
  onSubmit: (values: GroupClassFormValues) => void;
  onCancel: () => void;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  isLoading?: boolean;
  isSubmitted?: boolean;
  onViewClass?: () => void;
};
const durationOptions =
  GROUP_CLASS_DURATIONS.map((durationOption: number) => ({
    label: `${durationOption} mins`,
    value: `${durationOption}`,
  })) ?? [];

const TOP_LEVEL_ID = 'group-class-accordion-v2';

const GroupClassAccordion: React.FC<GroupClassAccordionProps> = ({
  mode,
  initialValues,
  onSubmit,
  onCancel,
  isExpanded,
  setIsExpanded,
  isLoading = false,
  isSubmitted = false,
  onViewClass,
}) => {
  const [formState, setFormState] = useState<GroupClassFormValues>({
    ageRanges: initialValues?.ageRanges || [],
    attendeeRoles: initialValues?.attendeeRoles || [],
    description: initialValues?.description || '',
    diagnoses: initialValues?.diagnoses || [],
    duration: initialValues?.duration || null,
    endDate: initialValues?.endDate ? new Date(initialValues.endDate) : null,
    frequency: (initialValues?.frequency as Recurrence) || null,
    hosts: initialValues?.hosts || [],
    iconName: initialValues?.iconName || null,
    isAllClassesRequired: initialValues?.mustAttendAll || false,
    isPromotedInEquipApp: initialValues?.isPromoted || false,
    isRegistrationEndsAfterFirstClass:
      Boolean(initialValues?.registrationEnds) || false,
    isSensitive: initialValues?.isSensitive || false,
    maximumAttendees: initialValues?.maximumAttendees ?? null,
    startDate: initialValues?.startDate
      ? new Date(initialValues.startDate)
      : null,
    title: initialValues?.title || '',
    treatmentModalities: initialValues?.treatmentModalities || [],
    treatmentPrograms: initialValues?.treatmentPrograms || [],
  });

  const updateFormState = <K extends keyof GroupClassFormValues>(
    key: K,
    value: GroupClassFormValues[K],
  ) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  };

  const getMinDate = (startDate?: Date): Date => {
    const today = new Date();
    return startDate && startDate >= today ? startDate : today;
  };

  const [isWarningCloseModalOpen, setIsWarningCloseModalOpen] = useState(false);
  const [
    isRemoveSensitiveConfirmModalOpen,
    setIsRemoveSensitiveConfirmModalOpen,
  ] = useState(false);

  const { showPromoteInEquipApp } = useFlags<LDFlags>();

  const { permissions } = useGlobalContext();
  const isSchedulingAdmin = permissions[MaudPermissions.MAUD_SCHEDULE_PROXY];

  const isAddMode = mode === 'add';
  const isEditMode = mode === 'edit';
  const isEndDateInvalid =
    isEditMode && initialValues?.endDate != null && !formState.endDate;

  const hasChanges =
    formState.title !== (initialValues?.title || '') ||
    formState.description !== (initialValues?.description || '') ||
    !isEqual(formState.hosts, initialValues?.hosts || []) ||
    formState.frequency !== (initialValues?.frequency || undefined) ||
    formState.duration !== initialValues?.duration ||
    wasDateChanged(initialValues?.endDate, formState.endDate) ||
    formState.maximumAttendees !== (initialValues?.maximumAttendees ?? null) ||
    !isEqual(
      formState.treatmentPrograms,
      initialValues?.treatmentPrograms || [],
    ) ||
    !isEqual(
      formState.treatmentModalities,
      initialValues?.treatmentModalities || [],
    ) ||
    !isEqual(formState.diagnoses, initialValues?.diagnoses || []) ||
    !isEqual(formState.ageRanges, initialValues?.ageRanges || []) ||
    formState.iconName !== (initialValues?.iconName || undefined) ||
    !isEqual(formState.attendeeRoles, initialValues?.attendeeRoles || []) ||
    formState.isPromotedInEquipApp !== Boolean(initialValues?.isPromoted) ||
    formState.isAllClassesRequired !== Boolean(initialValues?.mustAttendAll) ||
    formState.isSensitive !== Boolean(initialValues?.isSensitive) ||
    formState.isRegistrationEndsAfterFirstClass !==
      Boolean(initialValues?.registrationEnds);

  const { isValidMaximumGroupClassAttendees, groupClassMaxAttendeesLimit } =
    useGroupClassAttendeesLimit(formState?.maximumAttendees ?? null);

  const getMaxAttendeesErrorMessage = () => {
    if (formState.maximumAttendees === null) return null;
    if (isValidMaximumGroupClassAttendees) {
      return null;
    }
    return formState.maximumAttendees === 0
      ? `Max attendees should be more than 0`
      : `Max attendees should not be more than ${groupClassMaxAttendeesLimit}`;
  };

  const handleCloseWithoutWarning = () => {
    onCancel();
  };

  const handleCloseWithWarningCheck = () => {
    if (hasChanges) {
      setIsWarningCloseModalOpen(true);
    } else {
      onCancel();
    }
  };

  const handleOnAccordionDismiss = () => {
    if (isSubmitted) {
      handleCloseWithoutWarning();
    } else {
      handleCloseWithWarningCheck();
    }
  };

  const handleSetSensitive = (value: boolean) => {
    if (formState.isSensitive && isEditMode) {
      setIsRemoveSensitiveConfirmModalOpen(true);
    } else {
      updateFormState('isSensitive', value);
    }
  };

  const handleWarningModalClose = () => {
    setIsWarningCloseModalOpen(false);
  };

  const isButtonDisabled =
    !formState.title?.trim() ||
    formState.title.length > GROUP_CLASS_TITLE_LIMIT ||
    !formState.description?.trim() ||
    formState.description.length > GROUP_CLASS_DESCRIPTION_LIMIT ||
    isEmpty(formState.hosts) ||
    (isAddMode &&
      (!formState.frequency ||
        !formState.startDate ||
        !formState.duration ||
        !formState.iconName)) ||
    (isEditMode && (!formState.frequency || !formState.startDate)) ||
    isEndDateInvalid ||
    !!getMaxAttendeesErrorMessage() ||
    (hasChanges === false && isEditMode);

  const handleSubmit = () => {
    onSubmit(formState);
  };

  const renderFooter = () => {
    if (isSubmitted) return null;
    return (
      <ChakraFlex alignItems="center" justify="space-between" width="100%">
        <FireflyButton
          id={`${TOP_LEVEL_ID}__cancel`}
          leftIcon={FAIconName.xmarkLight}
          onClick={handleOnAccordionDismiss}
          variant="secondary"
        >
          Cancel
        </FireflyButton>
        <FireflyButton
          id={`${TOP_LEVEL_ID}__confirm`}
          isDisabled={isButtonDisabled}
          isLoading={isLoading}
          leftIcon={FAIconName.checkRegular}
          onClick={handleSubmit}
        >
          {isAddMode ? 'Create ' : 'Update '} class
        </FireflyButton>
      </ChakraFlex>
    );
  };

  const isRecurring = Boolean(
    formState.frequency && formState.frequency !== Recurrence.DOES_NOT_REPEAT,
  );

  return (
    <Accordion
      dismissMenu={handleOnAccordionDismiss}
      footer={renderFooter()}
      id={TOP_LEVEL_ID}
      isExpanded={isExpanded}
      panelContentMaxHeight={ACCORDION_HEIGHT_CALCULATED}
      panelMaxWidth={ACCORDION_WIDTH_LARGE}
      panelMinWidth={isSubmitted ? '100%' : ACCORDION_WIDTH_LARGE}
      setIsExpanded={setIsExpanded}
      title={`${isAddMode ? 'Create ' : 'Edit '} group`}
    >
      {isSubmitted ? (
        <GroupClassConfirmation
          closeAccordion={onCancel}
          confirmationType={isAddMode ? undefined : ConfirmationType.UPDATED}
          onSuccess={onViewClass}
        />
      ) : (
        <ChakraFlex
          borderRadius="12px"
          flexDirection="column"
          gap="36px"
          padding="18px 24px"
        >
          <ChakraFlex flexDirection="column" gap="12px">
            <Text variant="h5">Group information</Text>
            <GroupClassTitleField
              onChange={(value) => updateFormState('title', value)}
              value={formState.title}
            />
            <GroupClassDescriptionField
              onChange={(value) => updateFormState('description', value)}
              value={formState.description}
            />
            <GroupClassHostsField
              isDisabled={isLoading}
              onChange={(value: ProviderAutoCompleteOption[]) => {
                const convertedHosts: GroupClassHost[] = value.map(
                  (option) => ({
                    email: option.email,
                    externalId: option.value,
                    firstName: option.firstName || '',
                    lastName: option.lastName || '',
                    providerType: option.providerTypeDetails?.name || '',
                  }),
                );
                updateFormState('hosts', convertedHosts);
              }}
              value={formState.hosts.map(groupClassHostToAutoCompleteOption)}
            />
            {isAddMode && (
              <GroupIcons
                value={formState.iconName as GroupClassIllustrations}
                onChange={(value) => updateFormState('iconName', value)}
              />
            )}

            <ChakraSimpleGrid columns={{ base: 1, md: 2 }} gap="16px">
              {isAddMode && (
                <InputGroupV2
                  id={`${TOP_LEVEL_ID}__start-date`}
                  isRequired
                  label="Start Date"
                  shouldShowAsteriskWhenRequired
                >
                  <ChakraBox _focusWithin={{ zIndex: 3 }}>
                    <DatePickerInput
                      defaultDate={formState.startDate}
                      id={`${TOP_LEVEL_ID}__start-date`}
                      leftIcon={FAIconName.calendarDayRegular}
                      maxDate={formState.endDate}
                      minDate={new Date()}
                      onSubmit={(date: Date) => {
                        if (
                          formatFullDateString(DateTime.now()) ===
                          formatFullDateString(DateTime.fromJSDate(date))
                        ) {
                          updateFormState(
                            'startDate',
                            roundDateToNearestInterval(new Date(Date.now())),
                          );
                        } else {
                          updateFormState('startDate', date);
                        }
                      }}
                      placeholder="MM/DD/YYYY"
                    />
                  </ChakraBox>
                </InputGroupV2>
              )}
              {isAddMode && (
                <InputGroupV2
                  id={`${TOP_LEVEL_ID}__start-time`}
                  isRequired
                  label="Start time"
                  shouldShowAsteriskWhenRequired
                >
                  <ChakraBox _focusWithin={{ zIndex: 3 }}>
                    <DatePickerInput
                      defaultDate={formState.startDate}
                      id={`${TOP_LEVEL_ID}__start-time`}
                      isDisabled={!formState.startDate}
                      leftIcon={FAIconName.clockRegular}
                      maxDate={formState.endDate}
                      minDate={new Date()}
                      onSubmit={(date) => updateFormState('startDate', date)}
                      placeholder="9:30am"
                      showTimeOnly
                      timeIntervals={15}
                    />
                  </ChakraBox>
                </InputGroupV2>
              )}
              <GroupClassRecurrenceField
                isDisabled={isEditMode && !isRecurring}
                isEditMode={isEditMode}
                isRecurring={isRecurring}
                onChange={(value) => updateFormState('frequency', value)}
                startDate={formState.startDate}
                value={formState.frequency}
              />
              {isAddMode && (
                <InputGroupV2
                  id={`${TOP_LEVEL_ID}__duration`}
                  isRequired
                  label="Duration"
                  shouldShowAsteriskWhenRequired
                >
                  <UniversalSelect
                    id={`${TOP_LEVEL_ID}__duration-select`}
                    isSingleSelect
                    onChange={(option) =>
                      updateFormState('duration', Number(option.value))
                    }
                    options={durationOptions}
                    placeholder="Please choose"
                  />
                </InputGroupV2>
              )}
              <InputGroupV2 id={`${TOP_LEVEL_ID}__end-date`} label="End date">
                <ChakraBox _focusWithin={{ zIndex: 3 }}>
                  <DatePickerInput
                    defaultDate={formState.endDate}
                    id={`${TOP_LEVEL_ID}__end-date`}
                    isDisabled={isEditMode && !isRecurring}
                    isInvalid={isEndDateInvalid}
                    minDate={getMinDate(formState.startDate)}
                    onSubmit={(date) => {
                      if (date) {
                        updateFormState(
                          'endDate',
                          DateTime.fromJSDate(date).endOf('day').toJSDate(),
                        );
                      } else {
                        updateFormState('endDate', null);
                      }
                    }}
                    placeholder="MM/DD/YYYY"
                  />
                </ChakraBox>
              </InputGroupV2>
            </ChakraSimpleGrid>
            {showPromoteInEquipApp && isSchedulingAdmin && (
              <CheckboxV2
                id={`${TOP_LEVEL_ID}__promote-in-equip-app`}
                isChecked={formState.isPromotedInEquipApp}
                label={PROMOTE_THIS_GROUP_IN_EQUIP_APP}
                onChange={(value) =>
                  updateFormState('isPromotedInEquipApp', value)
                }
                size="md"
              />
            )}
          </ChakraFlex>
          <ChakraFlex flexDirection="column" gap="18px">
            <ChakraFlex flexDirection="column" gap="6px">
              <Text variant="h5">Registration Criteria</Text>
              <Text variant="body">
                Please select any limitations on registration below. If nothing
                is selected, the group will be available to all users.
              </Text>
            </ChakraFlex>
            <ChakraSimpleGrid columns={{ base: 1, md: 2 }} gap="16px">
              <GroupClassAttendeeTypesField
                onChange={(value) => updateFormState('attendeeRoles', value)}
                value={formState.attendeeRoles}
              />
              <GroupClassTreatmentProgramsField
                onChange={(value) =>
                  updateFormState('treatmentPrograms', value)
                }
                value={formState.treatmentPrograms}
              />
              <GroupClassModalitiesField
                onChange={(value) =>
                  updateFormState('treatmentModalities', value)
                }
                value={formState.treatmentModalities}
              />
              <GroupClassDiagnosisField
                onChange={(value) => updateFormState('diagnoses', value)}
                value={formState.diagnoses}
              />
              <GroupClassAgeRangeField
                onChange={(value) => updateFormState('ageRanges', value)}
                value={formState.ageRanges}
              />
              {isAddMode && (
                <InputGroupV2
                  id={`${TOP_LEVEL_ID}__max-attendees`}
                  label="Max attendees"
                >
                  <TextInputV2
                    id={`${TOP_LEVEL_ID}__max-attendees-input`}
                    isInvalid={!!getMaxAttendeesErrorMessage()}
                    onChange={(value) =>
                      updateFormState(
                        'maximumAttendees',
                        value === '' ? null : Number.parseInt(value, 10),
                      )
                    }
                    placeholder="Enter number"
                    type={TextInputV2Type.NUMBER}
                    value={formState.maximumAttendees?.toString() || ''}
                  />
                  {getMaxAttendeesErrorMessage() && (
                    <ValidationMessage
                      id={`${TOP_LEVEL_ID}__max-attendees-error-message`}
                      type={ValidationType.CRITICAL}
                      value={getMaxAttendeesErrorMessage()}
                    />
                  )}
                </InputGroupV2>
              )}
            </ChakraSimpleGrid>
            {isAddMode && (
              <>
                <CheckboxV2
                  id={`${TOP_LEVEL_ID}__registration-ends-after-first-class`}
                  isChecked={formState.isRegistrationEndsAfterFirstClass}
                  label={REGISTRATION_ENDS}
                  onChange={(value) =>
                    updateFormState('isRegistrationEndsAfterFirstClass', value)
                  }
                />
                <CheckboxV2
                  id={`${TOP_LEVEL_ID}__all-classes-required`}
                  isChecked={formState.isAllClassesRequired}
                  label={ALL_CLASSES_REQUIRED_V2}
                  onChange={(value) =>
                    updateFormState('isAllClassesRequired', value)
                  }
                />
              </>
            )}
            <ChakraFlex gap="8px">
              <CheckboxV2
                id={`${TOP_LEVEL_ID}__mark-as-sensitive`}
                isChecked={formState.isSensitive}
                label={SENSITIVE_GROUP_CLASS_LABEL}
                onChange={handleSetSensitive}
              />
              <Icon color="accent.blue" name={FAIconName.eyeSlashRegular} />
            </ChakraFlex>
          </ChakraFlex>
        </ChakraFlex>
      )}
      <RemoveSensitiveMarkConfirmationModal
        isOpen={isRemoveSensitiveConfirmModalOpen}
        onConfirm={() => {
          updateFormState('isSensitive', false);
          setIsRemoveSensitiveConfirmModalOpen(false);
        }}
        setIsOpen={setIsRemoveSensitiveConfirmModalOpen}
      />
      <DiscardChangesModalV2
        isOpen={isWarningCloseModalOpen}
        onClose={handleWarningModalClose}
        onConfirm={handleCloseWithoutWarning}
      />
    </Accordion>
  );
};

export default GroupClassAccordion;
